import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';

class CartPrecheckout extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /**
   * @param {InputEvent} event
   */
  updateCartAttributes = debounce(async (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const input = event.target;
    const value = input.value.trim();

    if (!value) {
      this.#clearError(input);
      await this.#save('');
      return;
    }

    if (this.#validateInput(input)) {
      await this.#save(this.#formatRut(value));
    }
  }, 400);

  /**
   * @param {FocusEvent} event
   */
  formatRut = async (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const input = event.target;
    const value = input.value.trim();

    if (!value) {
      this.#clearError(input);
      await this.#save('');
      return;
    }

    input.value = this.#formatRut(value);
    if (this.#validateInput(input)) {
      await this.#save(input.value);
    }
  };

  validateBeforeCheckout() {
    const input = this.querySelector('input[name="attributes[billing_rut]"]');
    if (!(input instanceof HTMLInputElement)) return true;

    const value = input.value.trim();
    const isRequired = this.dataset.requireRut === 'true' || input.required;

    if (isRequired && !value) {
      this.#showError(input, this.dataset.requiredMessage || 'El RUT es requerido.');
      this.#focusInput(input);
      return false;
    }

    if (value && !this.#validateInput(input)) {
      this.#focusInput(input);
      return false;
    }

    return true;
  }

  async updateCartAttributesBeforeCheckout() {
    const input = this.querySelector('input[name="attributes[billing_rut]"]');
    if (!(input instanceof HTMLInputElement)) return true;

    this.updateCartAttributes.cancel();
    const value = input.value.trim();
    return this.#save(value ? this.#formatRut(value) : '');
  }

  /**
   * @param {HTMLInputElement} input
   */
  #validateInput(input) {
    if (this.#isValidRut(input.value)) {
      this.#clearError(input);
      return true;
    }

    this.#showError(
      input,
      this.dataset.invalidMessage || 'RUT inválido. Verifique el formato y el dígito verificador.'
    );
    return false;
  }

  /**
   * @param {string} value
   */
  async #save(value) {
    this.#activeFetch?.abort();

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          attributes: {
            billing_rut: value,
          },
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Cart attributes update failed with status ${response.status}`);
      }

      this.#syncInputs(value);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return false;

      const input = this.querySelector('input[name="attributes[billing_rut]"]');
      if (input instanceof HTMLInputElement) {
        this.#showError(input, this.dataset.saveErrorMessage || 'No se pudo guardar el RUT. Intente nuevamente.');
      }
      return false;
    } finally {
      if (this.#activeFetch === abortController) {
        this.#activeFetch = null;
      }
    }

    return true;
  }

  /**
   * @param {string} value
   */
  #syncInputs(value) {
    document.querySelectorAll('input[name="attributes[billing_rut]"]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.value = value;
    });
  }

  /**
   * @param {HTMLInputElement} input
   * @param {string} message
   */
  #showError(input, message) {
    const errorId = `${input.id}-error`;
    let error = this.querySelector(`#${CSS.escape(errorId)}`);

    if (!(error instanceof HTMLElement)) {
      error = document.createElement('span');
      error.id = errorId;
      error.className = 'cart-precheckout__error';
      error.setAttribute('role', 'alert');
      input.insertAdjacentElement('afterend', error);
    }

    error.textContent = message;
    input.setAttribute('aria-invalid', 'true');

    const describedBy = new Set((input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
    describedBy.add(errorId);
    input.setAttribute('aria-describedby', [...describedBy].join(' '));
  }

  /**
   * @param {HTMLInputElement} input
   */
  #clearError(input) {
    const errorId = `${input.id}-error`;
    this.querySelector(`#${CSS.escape(errorId)}`)?.remove();
    input.removeAttribute('aria-invalid');

    const describedBy = (input.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter((id) => id && id !== errorId);
    input.setAttribute('aria-describedby', describedBy.join(' '));
  }

  /**
   * @param {HTMLInputElement} input
   */
  #focusInput(input) {
    requestAnimationFrame(() => {
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      input.focus();
    });
  }

  /**
   * @param {string} value
   */
  #cleanRut(value) {
    return value.replace(/^0+|[^0-9kK]+/g, '').toUpperCase();
  }

  /**
   * @param {string} value
   */
  #isValidRut(value) {
    const rut = this.#cleanRut(value);
    if (rut.length < 8 || rut.length > 9) return false;

    const numberPart = rut.slice(0, -1);
    const checkDigit = rut.slice(-1);
    if (!/^\d{7,8}$/.test(numberPart) || !/^[0-9K]$/.test(checkDigit)) return false;

    let number = Number.parseInt(numberPart, 10);
    let multiplierIndex = 0;
    let remainder = 1;

    while (number > 0) {
      remainder = (remainder + (number % 10) * (9 - (multiplierIndex++ % 6))) % 11;
      number = Math.floor(number / 10);
    }

    const expectedDigit = remainder > 0 ? String(remainder - 1) : 'K';
    return expectedDigit === checkDigit;
  }

  /**
   * @param {string} value
   */
  #formatRut(value) {
    const rut = this.#cleanRut(value);
    if (rut.length < 2) return rut;

    const numberPart = rut.slice(0, -1);
    const checkDigit = rut.slice(-1);
    const formattedNumber = numberPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    return `${formattedNumber}-${checkDigit}`;
  }
}

if (!customElements.get('cart-precheckout')) {
  customElements.define('cart-precheckout', CartPrecheckout);
}
